// Utility functions for TradeScope frontend

export const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

export const formatNumber = (num, options = {}) => {
  if (num === null || num === undefined || isNaN(num)) return '0'
  
  const {
    decimals = 0,
    locale = 'fr-FR',
    currency = null,
    compact = false
  } = options

  if (compact && Math.abs(num) >= 1000) {
    const units = ['', 'K', 'M', 'B', 'T']
    const unitIndex = Math.floor(Math.log10(Math.abs(num)) / 3)
    const scaledNum = num / Math.pow(1000, unitIndex)
    
    return `${scaledNum.toFixed(decimals)}${units[unitIndex]}`
  }

  if (currency) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(num)
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num)
}

export const formatCurrency = (amount, currency = 'DA') => {
  // Pour les Dinars Algériens (DA), ne pas utiliser le symbole € mais juste "DA"
  if (currency === 'DA' || !currency) {
    const formatted = formatNumber(amount, { decimals: 2 })
    return `${formatted} DA`
  }
  return formatNumber(amount, { currency, decimals: 2 })
}

export const formatCompactNumber = (num) => {
  return formatNumber(num, { compact: true, decimals: 1 })
}

/**
 * Format a DA value for table display — smart compact with full value in tooltip.
 * - < 10 000: full number with grouping (e.g. "8 521,30")
 * - >= 10 000: compact with 2 decimals (e.g. "52,43 K", "1,29 M", "29,52 M")
 * Returns { display, title } where title is the full formatted value for the tooltip.
 */
export const formatDATable = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return { display: '0', title: '0 DA' }
  const val = Number(amount)
  const fullFormatted = formatNumber(val, { decimals: 2 })

  if (Math.abs(val) < 10000) {
    return { display: fullFormatted, title: `${fullFormatted} DA` }
  }

  // Compact form
  const abs = Math.abs(val)
  let compact
  if (abs >= 1_000_000_000) compact = `${(val / 1_000_000_000).toFixed(2)} Md`
  else if (abs >= 1_000_000) compact = `${(val / 1_000_000).toFixed(2)} M`
  else if (abs >= 10_000) compact = `${(val / 1_000).toFixed(2)} K`

  return { display: compact, title: `${fullFormatted} DA` }
}

/**
 * Format a quantity/weight value for table display.
 * - < 10 000: full number with grouping
 * - >= 10 000: compact with 1 decimal + full value in tooltip
 */
export const formatQtyTable = (num) => {
  if (num === null || num === undefined || isNaN(num)) return { display: '0', title: '0' }
  const val = Number(num)
  const fullFormatted = formatNumber(val, { decimals: 0 })

  if (Math.abs(val) < 10000) {
    return { display: fullFormatted, title: fullFormatted }
  }

  const abs = Math.abs(val)
  let compact
  if (abs >= 1_000_000_000) compact = `${(val / 1_000_000_000).toFixed(2)} Md`
  else if (abs >= 1_000_000) compact = `${(val / 1_000_000).toFixed(2)} M`
  else if (abs >= 10_000) compact = `${(val / 1_000).toFixed(1)} K`

  return { display: compact, title: fullFormatted }
}

/**
 * Format a unit price — always 2 decimals with grouping, compact for very large.
 */
export const formatPriceTable = (num) => {
  if (num === null || num === undefined || isNaN(num)) return { display: '0,00', title: '0' }
  const val = Number(num)
  const fullFormatted = formatNumber(val, { decimals: 2 })

  if (Math.abs(val) < 100000) {
    return { display: fullFormatted, title: fullFormatted }
  }

  const abs = Math.abs(val)
  let compact
  if (abs >= 1_000_000) compact = `${(val / 1_000_000).toFixed(2)} M`
  else compact = `${(val / 1_000).toFixed(2)} K`

  return { display: compact, title: fullFormatted }
}

export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return '0%'
  return `${value.toFixed(decimals)}%`
}

export const formatDate = (date, format = 'short') => {
  if (!date) return ''
  
  const dateObj = typeof date === 'string' ? new Date(date) : date
  
  const options = {
    short: { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    },
    long: { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    },
    monthYear: { 
      year: 'numeric', 
      month: 'long' 
    }
  }
  
  return dateObj.toLocaleDateString('fr-FR', options[format] || options.short)
}

export const getChangeIndicator = (current, previous) => {
  if (!previous || previous === 0) return { value: 0, direction: 'neutral' }
  
  const change = ((current - previous) / previous) * 100
  const direction = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral'
  
  return { value: Math.abs(change), direction }
}

export const generateColors = (count) => {
  const colors = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#06b6d4', '#f97316', '#84cc16', '#ec4899', '#6366f1'
  ]
  
  if (count <= colors.length) {
    return colors.slice(0, count)
  }
  
  // Generate additional colors if needed
  const additionalColors = []
  for (let i = colors.length; i < count; i++) {
    const hue = (i * 137.508) % 360 // Golden angle approximation
    additionalColors.push(`hsl(${hue}, 70%, 50%)`)
  }
  
  return [...colors, ...additionalColors]
}

export const downloadCSV = (data, filename = 'export.csv') => {
  if (!data || data.length === 0) return
  
  const headers = Object.keys(data[0])
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header]
        // Escape commas and quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`
        }
        return value
      }).join(',')
    )
  ].join('\n')
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

export const classNames = (...classes) => {
  return classes.filter(Boolean).join(' ')
}

export const isValidDate = (date) => {
  return date instanceof Date && !isNaN(date)
}

export const parseDate = (dateString) => {
  if (!dateString) return null
  const date = new Date(dateString)
  return isValidDate(date) ? date : null
}

export const getDateRange = (period = '30d') => {
  const end = new Date()
  const start = new Date()
  
  switch (period) {
    case '7d':
      start.setDate(end.getDate() - 7)
      break
    case '30d':
      start.setDate(end.getDate() - 30)
      break
    case '90d':
      start.setDate(end.getDate() - 90)
      break
    case '1y':
      start.setFullYear(end.getFullYear() - 1)
      break
    default:
      start.setDate(end.getDate() - 30)
  }
  
  return { start, end }
}

export const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))
